import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiRequestError } from "../api/client";
import { useCurrentCase } from "../hooks/useCurrentCase";
import { CREDIT_RATES, UPLOAD_KINDS, type UploadKind } from "../../shared/constants";

interface UploadItem {
  id: string;
  kind: UploadKind;
  filename: string | null;
  content_type: string | null;
  size: number | null;
  url: string;
}

function isImage(ct: string | null): boolean {
  return !!ct && ct.startsWith("image/");
}

// No.4 写真・素材アップロード（R2へ実アップロード）。
export function UploadsPage() {
  const { caseRow, loading: caseLoading } = useCurrentCase();
  const navigate = useNavigate();
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKind, setBusyKind] = useState<string | null>(null);

  const reload = (caseId: string) =>
    api
      .get<{ uploads: UploadItem[] }>(`/api/cases/${caseId}/uploads`)
      .then((res) => setUploads(res.uploads))
      .finally(() => setLoading(false));

  useEffect(() => {
    if (caseRow) void reload(caseRow.id);
  }, [caseRow]);

  const onFiles = async (kind: UploadKind, files: FileList | null) => {
    if (!caseRow || !files || files.length === 0) return;
    setError(null);
    setBusyKind(kind);
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("kind", kind);
        form.append("file", file);
        await api.upload(`/api/cases/${caseRow.id}/uploads`, form);
      }
      await reload(caseRow.id);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "アップロードに失敗しました");
    } finally {
      setBusyKind(null);
    }
  };

  const onDelete = async (uploadId: string) => {
    if (!caseRow) return;
    try {
      await api.delete(`/api/cases/${caseRow.id}/uploads/${uploadId}`);
      setUploads((list) => list.filter((u) => u.id !== uploadId));
    } catch {
      /* noop */
    }
  };

  if (caseLoading) return <div className="page-center">読み込み中...</div>;
  if (!caseRow) return <div className="page-center">案件が見つかりません。</div>;

  const faceCount = uploads.filter((u) => u.kind === "face").length;

  return (
    <section className="wizard">
      <div className="wizard-head">
        <span className="step-tag">No.4</span>
        <h1>写真・素材アップロード</h1>
        <p className="muted">
          AIが最適なレイアウトで魅力を引き出します。アップロードは1ファイル{CREDIT_RATES.assetUpload}crを使用します。
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {UPLOAD_KINDS.map((k) => {
        const items = uploads.filter((u) => u.kind === k.value);
        return (
          <div key={k.value} className="card upload-section">
            <div className="upload-section-head">
              <h2>
                {k.label}{" "}
                <span className={`tag ${k.required ? "tag-req" : "tag-opt"}`}>
                  {k.required ? "必須" : "任意"}
                </span>
              </h2>
              <span className="muted small">{items.length} 件</span>
            </div>
            <p className="muted small">{k.hint}</p>
            <div className="upload-grid">
              <UploadDrop
                kind={k.value}
                busy={busyKind === k.value}
                onFiles={onFiles}
              />
              {items.map((u) => (
                <div key={u.id} className="upload-thumb">
                  <button
                    type="button"
                    className="thumb-del"
                    aria-label="削除"
                    onClick={() => onDelete(u.id)}
                  >
                    ×
                  </button>
                  {isImage(u.content_type) ? (
                    <img src={u.url} alt={u.filename ?? ""} />
                  ) : (
                    <div className="thumb-file">
                      <span className="thumb-ext">
                        {(u.content_type ?? "file").split("/").pop()}
                      </span>
                      <span className="muted small">{u.filename}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <div className="wizard-actions wizard-actions-end">
        <button
          className="btn btn-primary btn-lg"
          onClick={() => navigate("/profile-image")}
          disabled={loading || faceCount === 0}
          title={faceCount === 0 ? "本人の顔写真を1枚以上アップロードしてください" : undefined}
        >
          AIプロフィール画像へ進む →
        </button>
      </div>
      <p className="muted small center">
        素材はあとからでも追加・差し替えできます。
      </p>
      {faceCount === 0 && (
        <p className="muted small center">本人の顔写真を1枚以上アップロードすると次へ進めます。</p>
      )}
    </section>
  );
}

function UploadDrop({
  kind,
  busy,
  onFiles,
}: {
  kind: UploadKind;
  busy: boolean;
  onFiles: (kind: UploadKind, files: FileList | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div
      className="upload-drop"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        onFiles(kind, e.dataTransfer.files);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          onFiles(kind, e.target.files);
          e.target.value = "";
        }}
      />
      <div className="upload-drop-inner">
        <span className="upload-icon">⬆</span>
        <span className="muted small">ここにドラッグ＆ドロップ または</span>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          {busy ? "アップロード中..." : `ファイルを選択（1件${CREDIT_RATES.assetUpload}cr）`}
        </button>
      </div>
    </div>
  );
}
